import React from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cn from 'classnames';
import { withTheme } from 'providers/Theme.provider';
import STORE from 'store.singleton';

import Button from 'components/Button.component';
import Footer from 'components/Footer.component';
import { cognitoForgotPassword, withRcmPasswordReset } from 'utils/cognito.util';

import HeaderComponent from 'booking/common/Header';
import InputVerifyCode from 'common/InputVerifyCode';
import PasswordInputField from 'common/PasswordInputField';
import Spinner from 'common/Spinner.component';
import { validatePassword } from 'lib/validators';

import 'react-phone-number-input/style.css';
import CN from './ForgotPasswordWrap.module.scss';

const PasswordResetState = Object.freeze({
  VERIFICATION_PENDING: 'VERIFICATION_PENDING',
  VERIFICATION_ERROR: 'VERIFICATION_ERROR',
  VERIFICATION_REQUESTED: 'VERIFICATION_REQUESTED',
  PASSWORD_PENDING: 'PASSWORD_PENDING',
  PASSWORD_SUCCESS: 'PASSWORD_SUCCESS',
  PASSWORD_ERROR: 'PASSWORD_ERROR',
});

const ERROR = Object.freeze([PasswordResetState.PASSWORD_PENDING, PasswordResetState.PASSWORD_ERROR]);
const REQUESTED = Object.freeze([PasswordResetState.VERIFICATION_PENDING, PasswordResetState.VERIFICATION_REQUESTED]);

const ERROR_MESSAGES = Object.freeze({
  passwordMismatch:
    'Passwords don’t match. To save, please double check your spelling and ensure both passwords match.',
  passwordValid:
    'Password needs to be at least 8 characters in length and contain an uppercase character and a number.',
});

const StepTitle = ({ icon, title }) => (
  <div className={CN.subTitle}>
    <div className={icon} />
    {title}
  </div>
);

const ErrorMessage = ({ message }) => message && <span className={CN.error}>{message}</span>;

class ForgotPassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      code: Array(6).fill(null),
      isCodeComplete: false,
      password: '',
      isFooterButtonActive: false,
      inputHasError: false,
      state: PasswordResetState.VERIFICATION_PENDING,
      verificationError: '',
      passwordError: '',
      phone: '',
      confirmPassword: '',
      passwordMismatchError: '',
      isFormValid: false,
      isLoading: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  onResetPasswordClicked = () => {
    if (!this.state.isFooterButtonActive) return;
    if (this.state.isFormValid) {
      // TODO: either log the form submitted successfully, or just remove this code
    }

    const codeAsString = this.state.code.join('');
    const state = STORE.getState();

    if (this.props.setPassword) {
      this.props
        .setPassword(this.state.phone, codeAsString, this.state.password)
        .then(() => {
          const salonId = state?.booking?.salonDetails?.id;
          this.setState({ passwordError: '', state: PasswordResetState.PASSWORD_SUCCESS });
          this.props.history.push(`/salon/${salonId}`);
        })
        .catch(e => {
          const passwordError = e.message;
          this.setState({ passwordError, state: PasswordResetState.PASSWORD_ERROR });
        });
    }
  };

  validateCode = value => {
    // still have the original state value null, not finished typing yet
    if (0 <= value.findIndex(n => n === null)) return '';

    const codeRegex = /^[0-9]+$/;
    const codeAsString = value.join('');
    if (codeRegex.test(codeAsString) && codeAsString.length === 6) {
      this.setState({ inputHasError: false }, () => void this.checkPasswordButton());
      this.setState({ isFooterButtonActive: true });
      return '';
    }

    this.setState({ inputHasError: true }, () => void this.checkPasswordButton());

    return 'Please enter a valid code';
  };

  changePassword = value => void this.setState({ password: value, passwordError: '' }, this.validateForm);
  changeConfirmPassword = value => void this.setState({ confirmPassword: value }, this.validateForm);

  validateConfirmPassword = value =>
    void this.setState({ isFooterButtonActive: value === this.state.password }, this.validateForm);
  changeCode = value =>
    void this.setState({ code: value, isCodeComplete: value.every(num => num !== null && num !== '') });

  validateForm = () => {
    const { password, confirmPassword } = this.state;

    const isPasswordValid = validatePassword(password) === '';
    const passwordMismatchError =
      password && confirmPassword && password !== confirmPassword ? 'Passwords do not match' : '';

    this.setState({
      passwordMismatchError,
      isFormValid: isPasswordValid && !passwordMismatchError && confirmPassword,
      isFooterButtonActive: isPasswordValid && !passwordMismatchError && confirmPassword,
    });
  };

  validatePassword = value => {
    if (validatePassword(value)) {
      this.setState({
        passwordError: ERROR_MESSAGES.passwordValid, // confirm pass field
        isFooterButtonActive: false,
      });
      return ERROR_MESSAGES.passwordValid; // new pass field
    }
    this.setState({ passwordError: '', password: value }, this.validateForm);
    return '';
  };

  onInternationalPhone = (value, type) => {
    const phoneSymbolsRegex = /^[0-9+)(\s]*$/;
    if (phoneSymbolsRegex.test(value)) {
      if (type === 'inter') {
        this.setState({ inputHasError: false }, () => void this.checkButton());
        this.setState({ phone: value });
        return '';
      }
    }
    this.setState({ inputHasError: true }, () => void this.checkButton());
    return 'Error';
  };

  checkButton = () =>
    void this.setState({ isFooterButtonActive: !this.state.inputHasError && this.state.phone !== '' });

  checkPasswordButton = () => {
    const { isFooterButtonActive, inputHasError, password, code } = this.state;
    if (!isFooterButtonActive) return;
    this.setState({ isFooterButtonActive: !inputHasError && password !== '' && code !== '' });
  };

  nextStep = () =>
    void this.setState({
      verificationError: '',
      state: PasswordResetState.PASSWORD_PENDING,
      inputHasError: false,
    });

  checkAndsendVerificationCode = event => {
    const { cognitoUserPool } = this.props;
    const { isFooterButtonActive, phone: userName } = this.state;

    if (!isFooterButtonActive) return;
    if (event) event.preventDefault();
    this.setState({ isLoading: true });
    cognitoForgotPassword({ userName, userPool: cognitoUserPool })
      .then(() => {
        this.setState({
          verificationError: '',
          state: PasswordResetState.PASSWORD_PENDING,
          inputHasError: false,
          isFooterButtonActive: false,
          isLoading: false,
        });
      })
      .catch(error => {
        if (error.code === 'UserNotFoundException') {
          this.setState({
            verificationError: 'User not found',
            state: PasswordResetState.VERIFICATION_ERROR,
            isLoading: false,
          });
        } else {
          this.setState({
            verificationError: error.message,
            state: PasswordResetState.VERIFICATION_ERROR,
            isLoading: false,
          });
        }
      });
    // }
  };

  render() {
    const { isDarkMode, isNeutralMode } = this.props;
    const {
      isLoading,
      isCodeComplete,
      verificationError,
      passwordError,
      passwordMismatchError,
      state: cognitoState,
      isFooterButtonActive,
      isFormValid,
      phone,
    } = this.state;

    const isSubmitDisabled = !isFooterButtonActive || !isFormValid;

    const isRequestedOrPending = REQUESTED.includes(cognitoState);
    const isErrorOrPending = ERROR.includes(cognitoState);
    const isError = cognitoState === PasswordResetState.VERIFICATION_ERROR;

    return (
      <div
        data-bem="ForgotPassword"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
          [CN.neutral]: isNeutralMode,
        })}
      >
        {isLoading && <Spinner variant="overlay" />}
        {isRequestedOrPending && (
          <div className={CN.wrapper}>
            <HeaderComponent history={this.props.history} />

            <StepTitle icon={CN.icon} title="Password reset" />

            <div className={CN.formContainer}>
              <div className={CN.resetInfo}>Enter your mobile number and we'll send a reset code to your email</div>
              <div
                className={cn({
                  [CN.phoneContainer]: true,
                  [CN.hasError]: phone && !isValidPhoneNumber(phone),
                })}
              >
                <label htmlFor="phone" className={CN.inputLabel}>
                  Mobile number
                </label>
                <PhoneInput
                  international
                  defaultCountry="GB"
                  placeholder="Enter phone number"
                  value={phone}
                  onChange={value => void this.onInternationalPhone(value, 'inter')}
                />
                {phone && !isValidPhoneNumber(phone) && (
                  <span className={CN.error}>Please enter valid phone number</span>
                )}
              </div>
              <span className={CN.resetNote}>
                If you do not receive a code, please contact us with your name and phone number so we can verify your
                account and send you a new code.
              </span>
            </div>

            <Footer data-bem="ForgotPassword__footer-VERIFICATION_REQUESTED">
              <Button
                variant="primary"
                width="fixed"
                onClick={this.checkAndsendVerificationCode}
                disabled={!isFooterButtonActive}
              >
                SEND CODE
              </Button>
            </Footer>
          </div>
        )}
        {isError && (
          <div className={CN.wrapper}>
            <HeaderComponent
              history={this.props.history}
              subtitle="We couldn't send you a reset code, if you already have one, you can continue"
            />
            <StepTitle icon={CN.icon} title="Reset went wrong " />
            <ErrorMessage message={verificationError} />
            <Footer data-bem="ForgotPassword__footer-VERIFICATION_ERROR">
              <Button variant="primary" width="fixed" onClick={this.nextStep}>
                ERROR
              </Button>
            </Footer>
          </div>
        )}
        {isErrorOrPending && (
          <div className={CN.wrapper}>
            <HeaderComponent />
            <StepTitle icon={CN.icon} title=" Enter your 6 digit code" />
            <div className={CN.codeInfo}>
              The password reset code has been sent<span className={CN.bold}>to your mobile number</span>
            </div>
            <div className={CN.inputLabel}>Code</div>
            <div className={CN.formContainer}>
              <InputVerifyCode
                id="CodeInput"
                label=""
                type="number"
                inputValue=""
                onBlurHandler={this.changeCode}
                validateBlur={this.validateCode}
              />

              {verificationError === '' && isCodeComplete && (
                <div className={CN.formContainer}>
                  <div>
                    <div className={CN.rulesTitle}>Your password must contain:</div>
                    <ul className={CN.passwordRules}>
                      <li> at least 8 characters</li>
                      <li> at least 1 lowercase letter</li>
                      <li> at least 1 uppercase letter</li>
                      <li> at least 1 number</li>
                    </ul>
                  </div>

                  <PasswordInputField
                    label="New Password"
                    data={{
                      id: 'PasswordInput',
                      type: 'password',
                      placeholder: '●●●●●●●●',
                      inputValue: '',
                    }}
                    validateBlur={this.validatePassword}
                    onBlurHandler={this.changePassword}
                    allowAutocomplete
                  />

                  <PasswordInputField
                    label="Re-enter new password"
                    data={{
                      id: 'ConfirmPasswordInput',
                      type: 'password',
                      placeholder: '●●●●●●●●',
                      inputValue: '',
                    }}
                    validateBlur={this.validateConfirmPassword}
                    onBlurHandler={this.changeConfirmPassword}
                    allowAutocomplete
                  />

                  <ErrorMessage message={passwordMismatchError ? ERROR_MESSAGES.passwordMismatch : ''} />
                  <ErrorMessage message={passwordError} />
                </div>
              )}
            </div>

            <Footer data-bem="ForgotPassword__footer-PASSWORD_ERROR">
              <Button variant="primary" width="fixed" onClick={this.onResetPasswordClicked} disabled={isSubmitDisabled}>
                SUBMIT
              </Button>
            </Footer>
          </div>
        )}
      </div>
    );
  }
}

const ForgotPasswordHoc = withRcmPasswordReset(
  withRouter(
    withTheme()(
      connect(state => ({
        cognitoState: state.cognito.state,
        cognitoUserPool: state.cognito.userPool,
      }))(ForgotPassword),
    ),
  ),
);

export default ForgotPasswordHoc;
